import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { createLoadOptions, useDebounce } from "../../hooks/useDebounce";
import { useFilterSearch } from "../../hooks/useFilterSearch";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { EntityAcl, EntityAclPermission } from "../../http/types/entityAcl";
import { SearchUser } from "../../http/types/users";
import { useEntityAclApi } from "../../http/useEntityAcl";
import { useUsersApi } from "../../http/useUsers";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import Button from "./Button";
import styles from "./EntityAccessesContent.module.css";
import Form from "./Form";
import FormField from "./FormField";
import FormFieldsContainer from "./FormFieldsContainer";
import InlineBadgeEditor, { InlineBadgeEditorOption } from "./InlineBadgeEditor";
import { getDeleteAction } from "./ListItemMenu";
import ModalDialog from "./ModalDialog";
import { CustomAsyncSelect } from "./select/CustomSelect";
import Switch from "./Switch";
import Table from "./Table";
import TableHeading from "./TableHeading";

interface EntityAccessesContentProps {
  entityType: string;
  entityId: string;
  onReady?: () => void;
}

interface AddAccessForm {
  user: SearchUser | null;
  allow: boolean;
}

const EntityAccessesContent: FC<EntityAccessesContentProps> = ({ entityType, entityId, onReady }) => {
  const i18n = useI18n();
  const entityAclApi = useEntityAclApi();
  const usersApi = useUsersApi();

  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, `entityAccesses_${entityType}`);
  const userSearchTerm = useRef("");
  const userAborter = useRef(new AbortController());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [accessToDelete, setAccessToDelete] = useState<EntityAcl>();
  const [defaultUserOptions, setDefaultUserOptions] = useState<SearchUser[]>([]);

  const debouncedValue = useDebounce(searchTerm);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error, mutate } = entityAclApi.useSearchEntityAcl(
    { entityType, entityId },
    query,
    aborter.current.signal,
  );

  const isInitialized = useInitializer(data || error);

  useEffect(() => {
    isInitialized && onReady?.();
  }, [isInitialized]);

  const loadUserOptions = createLoadOptions(userAborter, userSearchTerm, usersApi.getAdminUsers);

  useEffect(() => {
    loadUserOptions("").then(setDefaultUserOptions);
  }, []);

  const accessPermissionOptions: InlineBadgeEditorOption<EntityAclPermission>[] = [
    { value: "allow", label: i18n.translation.common.allow, color: "success" },
    { value: "deny", label: i18n.translation.common.deny, color: "error" },
  ];

  const onUpdatePermission = (item: EntityAcl, permission: EntityAclPermission) => {
    setIsSubmitting(true);

    // Optimistically reflect the change locally so the badge switches immediately, instead of
    // waiting on the PUT + revalidate round-trip — otherwise a second toggle sent before that
    // round-trip resolves still sees the old (stale) permission and re-sends the same target value,
    // making the UI look like it ignored the change.
    mutate(
      (current) =>
        current && {
          ...current,
          items: current.items.map((i) => (i.id === item.id ? { ...i, permission } : i)),
        },
      { revalidate: false },
    );

    entityAclApi
      .updateEntityAcl(item.id, { permission })
      // .then(() => dispatch(showSuccessNotification(i18n.translation)))
      .catch((err) => {
        dispatch(showErrorNotification(i18n, err));
        mutate();
      })
      .finally(() => setIsSubmitting(false));
  };

  const onAddSubmit = () => async (formData: AddAccessForm) => {
    if (!formData.user) {
      return;
    }

    setIsSubmitting(true);
    try {
      await entityAclApi.insertEntityAcl({
        entityType,
        entityId,
        userId: formData.user.id,
        permission: formData.allow ? "allow" : "deny",
      });
      setAddModalOpen(false);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <TableHeading
        className={styles.heading}
        isSearchable
        searchValue={searchTerm}
        searchPlaceholder={i18n.translation.users.search}
        searchButtons={
          <Button glyph={SvgAdd} buttonProps={{ onClick: () => setAddModalOpen(true), disabled: !!error }}>
            {i18n.translation.users.add}
          </Button>
        }
        onSearchChange={handleSearchChange}
      />
      <Table<EntityAcl>
        filters={filters}
        isTall
        head={
          <tr>
            <th align="left">{i18n.translation.users.usersName}</th>
            <th align="left">{i18n.translation.users.usersEmail}</th>
            <th align="left">{i18n.translation.entityAccesses.accessColumn}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              {item.user.firstName} {item.user.lastName}
            </td>
            <td>{item.user.email}</td>
            <td onClick={(e) => e.stopPropagation()}>
              <InlineBadgeEditor
                value={item.permission}
                options={accessPermissionOptions}
                onChange={(permission) => onUpdatePermission(item, permission)}
              />
            </td>
          </>
        )}
        rowActions={(item) => [getDeleteAction(i18n, () => setAccessToDelete(item))]}
        total={data?.total}
        error={!!error}
      />

      {isAddModalOpen && (
        <ModalDialog title={i18n.translation.entityAccesses.add} onClose={() => setAddModalOpen(false)}>
          <Form<AddAccessForm>
            defaultValues={{
              user: null,
              allow: true,
            }}
            submitText={i18n.translation.common.add}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => setAddModalOpen(false) }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={onAddSubmit}
          >
            {({ control, setValue, watch }) => {
              const allow = watch("allow");

              return (
                <FormFieldsContainer fullWidth>
                  <Controller
                    name="user"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.users.usersEmail} error={fieldState.error}>
                        {({ labelId }) => (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultUserOptions}
                            loadOptions={loadUserOptions}
                            isClearable
                            getOptionLabel={(user) => `${user.firstName} ${user.lastName} (${user.email})`}
                            getOptionValue={(user) => user.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("user", value ?? null)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <FormField label={i18n.translation.common.allow} isOptional>
                    {({ labelId }) => (
                      <Switch id={labelId} checked={allow} onChange={(checked) => setValue("allow", checked)} />
                    )}
                  </FormField>
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}

      {!!accessToDelete && (
        <ModalDialog
          onClose={() => setAccessToDelete(undefined)}
          title={i18n.translation.users.remove}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setAccessToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                entityAclApi
                  .deleteEntityAcl(accessToDelete.id)
                  .then(() => {
                    setAccessToDelete(undefined);
                    dispatch(showSuccessNotification(i18n.translation));
                  })
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.entityAccesses.questions.remove}
        </ModalDialog>
      )}
    </>
  );
};

export default EntityAccessesContent;
